/**
 * KimlikPasaportModal.jsx — yabancı firmanın personeli için kimlik / pasaport penceresi
 * (backlog #320, 2026-10-11; plan: "yabancı dekoratör personel sayfasında işlem yaparken kimlik
 * yükleme/doğrulama modalı olacak, geri kalan belgeler disabled olacak").
 *
 * Yabancı firmanın çalışanı KVKK + Kimlik / Pasaport belgesi (file_type 90, "kimlik kartı ya da pasaport
 * görseli") + firmanın bir onaylı firma belgesiyle kart alır (backend #314, YABANCI yolu). Bu pencere:
 *   - pasaport numarası ve veren ülke (PUT /personnel/{id}/pasaport) — firma girer, personel de girebilir
 *   - kimlik / pasaport görseli: burada yüklenir ya da değiştirilir (#511: POST /personnel/dosyaekle, file_type 90)
 *   - kimlik doğrulama durumu: SAKA personeli doğrular (firma kendi çalışanını doğrulayamaz)
 *
 * #591 (Deniz 2026-10-30, backend #590): yabancı kişinin numarası bir kez sorulur. Personel Ekle'de yazılan numara pasaport ise
 * (11 hane değilse) bu pencere onu hazır getirir (`pasaportNo`); firma SAKA doğrulayana kadar düzeltebilir, doğrulamadan sonra pencerenin
 * tamamı salt okunur (backend zaten 409 verir). Kartın kimlik numarası pasaportsa (`pasaportKimlikNo`) değişiklik kartın numarasını da
 * değiştirir; kartta 99'lu / T.C. numara varsa pasaport buraya ilk ve tek kez yazılır. Hata metinleri backend'den olduğu gibi gelir (TR / EN).
 *
 * #700 (backend #699): yüklenmiş pasaport görseli "Pasaportu kaldır" ile (onaydan sonra) doğrudan kaldırılır — Havuz yoluna gerek yok.
 * Yükle / değiştir ile aynı koşul: kimlik doğrulanmışsa (durum 1) düğme yok. Backend'in reddi (409 / 404) pencerenin içinde olduğu gibi çıkar.
 *
 * #702: pencere KİŞİNİN uyruğuna uyar, firmanınkine değil. Yabancı firmanın ekibindeki T.C. vatandaşından (uyruk 1) pasaport numarası ve veren ülke
 * sorulmaz, yalnızca kimlik görseli; başlık ve metinler "Kimlik" der (`kimlikPasaport.yerli.*`) ve "Pasaportu Kaydet" düğmesi çizilmez. Kayıtlı bir
 * pasaportu olan T.C. kişi eski görünümde kalır (`yerliKimlikMi`): elindeki hiçbir şey gizlenmez. WAS: her kişi için numara + ülke + "Pasaportu ...".
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { Trash2, Upload } from "lucide-react";
import { getKimlikDogrulama, getUlkeler, setPasaport } from "../../services/portalService";
import { kimlikBelgeTuruLabel, kimlikBelgesiniKaldir, uploadKimlikBelgesi } from "../../services/kisiAdminService";
import { dosyaHatasi } from "../../services/personelBelgeService";
import KisiDosyaLink from "./KisiDosyaLink";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { yerliKimlikMi } from "./YabanciKatilimciParcalari";
import { useTranslation } from "react-i18next";

// WAS (until #511): reactstrap <Badge color="success|secondary|…"> — bu temada `success` gri, `secondary` yeşil-mavi çıkıyor, yani rozet durumu
// tersine söylüyordu. Renkler listedeki kutularla aynı sabit hex'ler (durum 0 doğrulanmadı gri · 1 doğrulandı yeşil · 2 reddedildi kırmızı).
// #719: 3 = belge SAKA'nın masasında onaylı, kişi girişte doğrulanacak (KisiKunyeSeridi.KIMLIK_HEX ile aynı mavi).
const DOGRULAMA_RENK = { 0: NEUTRAL_GRAY, 1: STATUS_HEX.success, 2: STATUS_HEX.danger, 3: STATUS_HEX.info };
// Rozet metni arayüz dilinde `kimlikPasaport` anahtarlarından gelir. WAS (until #719): backend'in Türkçe `durumAdi`si her durumda yazılırdı.
const DOGRULAMA_METIN = { 0: "dogrulanmadi", 1: "dogrulandi", 2: "reddedildi", 3: "alandaDogrulanacak" };
const BELGE_RENK = { 0: STATUS_HEX.warning, 1: STATUS_HEX.success, 2: STATUS_HEX.danger };

function Rozet({ renk, children }) {
  return <span className="badge text-white" style={{ backgroundColor: renk }}>{children}</span>;
}

/**
 * @param fuarId  belgenin etiketlendiği fuar (üst çubuktaki; `fuar_id` form alanı, #387). Verilmezse backend kullanıcının seçili fuarını okur.
 * Pasaport DOSYASI burada yüklenir (#511): `POST /personnel/dosyaekle/{id}` file_type 90 — firmanın yüklemesi tarihsiz ve Beklemede
 * yazılır, FDS personeli onaylayıp kimliği doğrulayana kadar hiçbir şeye sayılmaz. WAS (until #511): "Yükle / görüntüle" bağlantısı
 * kişi sayfasına gidiyordu ama orada kimlik belgesi kartı yoktu, yani firma tarafında pasaport yüklemenin bir yolu görünmüyordu.
 */
export default function KimlikPasaportModal({ kisi, belge, fuarId = null, onClose, onSaved }) {
  const { t } = useTranslation("components");
  const [dogrulama, setDogrulama] = useState(null);
  const [ulkeler, setUlkeler] = useState([]);
  const [no, setNo] = useState("");
  const [ulke, setUlke] = useState("");
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [dosyaYukleniyor, setDosyaYukleniyor] = useState(false);
  // #700: "Pasaportu kaldır" önce onay ister; kaldırıldıktan sonra liste satırından gelen eski `belge` ikinci kez gösterilmesin.
  const [kaldirOnay, setKaldirOnay] = useState(false);
  const [kaldiriyor, setKaldiriyor] = useState(false);
  const [belgeKaldirildi, setBelgeKaldirildi] = useState(false);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const dosyaRef = useRef(null);
  // #702: T.C. kişi → "Kimlik" metinleri, numara / ülke yok. `a` = pasaport metni anahtarı, `b` = `yerli` altındaki karşılığı.
  const yerli = yerliKimlikMi(dogrulama ? { kimlikDogrulama: dogrulama } : null); // `dogrulama` = PersonKimlikDogrulamaDTO (uyrukId, pasaportNo, ...)
  const m = (a, b) => t(yerli ? `kimlikPasaport.yerli.${b}` : `kimlikPasaport.${a}`);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const [d, u] = await Promise.all([getKimlikDogrulama(kisi.personId), getUlkeler().catch(() => [])]);
        if (ignore) return;
        setDogrulama(d);
        setNo(d?.pasaportNo ?? "");
        setUlke(d?.pasaportUlkeId != null ? String(d.pasaportUlkeId) : "");
        setUlkeler(Array.isArray(u) ? u : []);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [kisi.personId]);

  async function kaydet() {
    setKaydediyor(true);
    setHata(null);
    setBilgi(null);
    try {
      const out = await setPasaport(kisi.personId, no.trim(), ulke ? Number(ulke) : null);
      if (out?.data) {
        setDogrulama(out.data);
        // Backend boşlukları atıp büyük harfe çevirir; kutu kayıtlı değeri göstersin, "değişti" bayrağı takılı kalmasın.
        setNo(out.data.pasaportNo ?? "");
      }
      setBilgi(t("kimlikPasaport.pasaportBilgisiKaydedildi"));
      onSaved?.();
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  async function dosyaYukle(dosya) {
    const mesaj = dosyaHatasi(dosya);
    if (mesaj) { setHata(mesaj); return; }
    setDosyaYukleniyor(true);
    setHata(null);
    setBilgi(null);
    try {
      await uploadKimlikBelgesi(kisi.personId, dosya, { fuarId });
      // Backend yüklenen dosyanın durumunu satıra yazmaz (tip 90 yalnızca bir kural istiyorsa `documents{}`ta çıkar):
      // taze durum kimlik-doğrulama okumasının `kimlikBelgesi` alanından gelir (#469).
      setDogrulama(await getKimlikDogrulama(kisi.personId));
      setBelgeKaldirildi(false);
      setBilgi(m("pasaportYuklendi", "yuklendi"));
      onSaved?.();
    } catch (e) {
      setHata(e.message);
    } finally {
      setDosyaYukleniyor(false);
    }
  }

  async function pasaportuKaldir() {
    setKaldiriyor(true);
    setHata(null);
    setBilgi(null);
    try {
      // `data` = taze kimlik-doğrulama gövdesi (durum 3 → 0 olmuş olabilir); gelmediyse yeniden okunur.
      const taze = await kimlikBelgesiniKaldir(kisi.personId);
      setDogrulama(taze && typeof taze === "object" ? taze : await getKimlikDogrulama(kisi.personId));
      setBelgeKaldirildi(true);
      setKaldirOnay(false);
      setBilgi(m("pasaportKaldirildi", "kaldirildi"));
      onSaved?.();
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaldiriyor(false);
    }
  }

  const degisti = (dogrulama?.pasaportNo ?? "") !== no.trim()
    || String(dogrulama?.pasaportUlkeId ?? "") !== ulke;
  // Yüklemeden sonra taze olan `kimlikBelgesi`; liste satırından gelen `belge` yalnızca ilk açılışın değeri.
  const belgeNow = belgeKaldirildi ? (dogrulama?.kimlikBelgesi ?? null) : (dogrulama?.kimlikBelgesi ?? belge);
  const belgeDurumu = belgeNow?.status;
  const mesgul = kaydediyor || dosyaYukleniyor || kaldiriyor;
  // Doğrulanmış kimlik firma tarafından değiştirilmez (backend 409); upload düğmesi de aynı kuralla gizli.
  const dogrulandi = dogrulama?.durum === 1;
  const noYardim = dogrulama?.pasaportKimlikNo ? t("kimlikPasaport.numaraKimlikNoYardim")
    : !dogrulama?.pasaportNo ? t("kimlikPasaport.numaraKartlaIlgisizYardim") : null;

  return (
    <Modal isOpen toggle={() => !mesgul && onClose()}>
      <ModalHeader toggle={() => !mesgul && onClose()}>
        {m("kimlikPasaport", "baslik")}{" "}{[kisi.name, kisi.surname].filter(Boolean).join(" ")}
      </ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        {bilgi && <Alert color="success" className="small py-2">{bilgi}</Alert>}
        {yukleniyor ? <Spinner size="sm" /> : (
          <>
            <p className="small text-muted">
              {m("yabanciFirmaCalisaniKvkkKimlik", "aciklama")}
            </p>
            {/* #702: a T.C. person is not asked for a passport number or country (WAS: always drawn). */}
            {!yerli && <Row className="g-2 mb-3">
              <Col md={6}>
                <Label className="small mb-1" for="kp-no">{t("kimlikPasaport.pasaportNumarasi")}</Label>
                <Input id="kp-no" bsSize="sm" value={no} maxLength={50} disabled={kaydediyor || dogrulandi}
                       onChange={(e) => setNo(e.target.value)} />
              </Col>
              <Col md={6}>
                <Label className="small mb-1" for="kp-ulke">{t("kimlikPasaport.verenUlke")}</Label>
                {ulkeler.length > 0 ? (
                  <Input id="kp-ulke" type="select" bsSize="sm" value={ulke} disabled={kaydediyor || dogrulandi}
                         onChange={(e) => setUlke(e.target.value)}>
                    <option value="">{t("kimlikPasaport.secilmedi")}</option>
                    {ulkeler.map((u) => <option key={u.countryId} value={u.countryId}>{u.countryName}</option>)}
                  </Input>
                ) : (
                  <Input id="kp-ulke" bsSize="sm" placeholder={t("kimlikPasaport.ulkeId")} value={ulke} disabled={kaydediyor || dogrulandi}
                         onChange={(e) => setUlke(e.target.value.replace(/\D/g, ""))} />
                )}
              </Col>
              {(dogrulandi || noYardim) && (
                <Col xs={12}>
                  <div className="small text-muted">{dogrulandi ? t("kimlikPasaport.dogrulandiDegistirilemez") : noYardim}</div>
                </Col>
              )}
            </Row>}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              <span className="small fw-semibold">{m("kimlikPasaportGorseli", "gorsel")}</span>
              <Rozet renk={BELGE_RENK[belgeDurumu] ?? NEUTRAL_GRAY}>
                {belgeDurumu === 1 ? t("kimlikPasaport.onayli") : belgeDurumu === 2 ? t("kimlikPasaport.reddedildi") : belgeDurumu === 0 ? t("kimlikPasaport.incelemede") : t("kimlikPasaport.yuklenmedi")}
              </Rozet>
              {belgeNow?.fileId != null && (
                <KisiDosyaLink personId={kisi.personId} fileId={belgeNow.fileId} label={t("kimlikPasaport.ac")} />
              )}
              {/* Doğrulanmış kimliğin belgesi firma tarafından değiştirilmez. */}
              {!dogrulandi && (
                <>
                  <input
                    ref={dosyaRef} type="file" className="d-none" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const dosya = e.target.files?.[0];
                      e.target.value = "";
                      if (dosya) dosyaYukle(dosya);
                    }}
                  />
                  <Button color="primary" outline size="sm" disabled={mesgul} onClick={() => dosyaRef.current?.click()}>
                    {dosyaYukleniyor
                      ? <Spinner size="sm" />
                      : <><Upload size={14} className="me-1" />{belgeNow?.fileId != null ? m("pasaportuDegistir", "degistir") : m("pasaportuYukle", "yukle")}</>}
                  </Button>
                  {/* #700: yüklü görsel varsa kaldırılabilir (onaydan sonra); doğrulanmış kimlikte yukarıdaki koşulla zaten gizli. */}
                  {belgeNow?.fileId != null && (
                    <Button color="danger" outline size="sm" disabled={mesgul || kaldirOnay} onClick={() => setKaldirOnay(true)}>
                      <Trash2 size={14} className="me-1" />{m("pasaportuKaldir", "kaldir")}
                    </Button>
                  )}
                </>
              )}
            </div>
            {kaldirOnay && (
              <Alert fade={false} color="warning" className="small py-2 d-flex align-items-center gap-2 flex-wrap">
                <span className="me-auto">{m("kaldirOnayMetni", "kaldirOnayMetni")}</span>
                <Button color="danger" size="sm" disabled={mesgul} onClick={pasaportuKaldir}>
                  {kaldiriyor ? <Spinner size="sm" /> : t("kimlikPasaport.evetKaldir")}
                </Button>
                <Button color="secondary" outline size="sm" disabled={mesgul} onClick={() => setKaldirOnay(false)}>
                  {t("kimlikPasaport.vazgec")}
                </Button>
              </Alert>
            )}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="small fw-semibold">{t("kimlikPasaport.kimlikDogrulama")}</span>
              <Rozet renk={DOGRULAMA_RENK[dogrulama?.durum] ?? NEUTRAL_GRAY}>{t(`kimlikPasaport.${DOGRULAMA_METIN[dogrulama?.durum] ?? "dogrulanmadi"}`)}</Rozet>
              {/* WAS (until #470): ({dogrulama.belgeTuru === "PASAPORT" ? "pasaportla" : "TC kimlikle"}) — iki değer varken; altı tip artık ortak etiketlerden. */}
              {dogrulama?.belgeTuru && <span className="small text-muted">({kimlikBelgeTuruLabel(dogrulama.belgeTuru)})</span>}
              <span className="small text-muted">{t("kimlikPasaport.sakaPersoneliDogrular")}</span>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" disabled={mesgul} onClick={onClose}>{t("common:kapat")}</Button>
        {!dogrulandi && !yerli && (
          <Button color="primary" size="sm" disabled={mesgul || yukleniyor || !degisti} onClick={kaydet}>
            {kaydediyor ? <Spinner size="sm" /> : t("kimlikPasaport.pasaportuKaydet")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
